import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, GraduationCap, BookOpen, Award } from 'lucide-react';

export const ExpandableStandardCard = ({ standard, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const Icon = standard.icon;

  const trainingLevels = [
    {
      id: 'awareness',
      title: 'Awareness Training',
      duration: standard.trainingDuration?.awareness || '1 Day',
      icon: GraduationCap,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      route: `/training/${standard.slug}/awareness`
    },
    {
      id: 'internal-auditor',
      title: 'Internal Auditor Training',
      duration: standard.trainingDuration?.internalAuditor || '3 Days',
      icon: BookOpen,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      route: `/training/${standard.slug}/internal-auditor`
    },
    {
      id: 'lead-auditor',
      title: 'Lead Auditor Training',
      duration: standard.trainingDuration?.leadAuditor || '5 Days',
      icon: Award,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      route: `/training/${standard.slug}/lead-auditor`
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const expandedVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const contentVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const handleCardClick = (e) => {
    e.stopPropagation();
    setIsExpanded(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsExpanded(false);
  };

  const handleTrainingClick = (route, trainingId) => {
    // ISO 9001 routes
    if (standard.slug === 'iso-9001') {
      if (trainingId === 'awareness') {
        navigate('/ISO-9001-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/Internal-Auditor-ISO-9001-Training');
      } else if (trainingId === 'lead-auditor') {
        navigate('/Lead-Auditor-Certification-ISO-9001');
      } else {
        navigate(route);
      }
    }
    // ISO 14001 routes
    else if (standard.slug === 'iso-14001') {
      if (trainingId === 'awareness') {
        navigate('/ISO-14001-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/ISO-14001-Internal-Auditor-Course');
      } else if (trainingId === 'lead-auditor') {
        navigate('/ISO-14001-Lead-Auditor-Course');
      } else {
        navigate(route);
      }
    }
    // ISO 27001 routes
    else if (standard.slug === 'iso-27001') {
      if (trainingId === 'awareness') {
        navigate('/ISO-27001-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/ISO-27001-Internal-Auditor-Course');
      } else if (trainingId === 'lead-auditor') {
        navigate('/ISO-27001-Lead-Auditor-Course');
      } else {
        navigate(route);
      }
    }
    // ISO 45001 routes
    else if (standard.slug === 'iso-45001') {
      if (trainingId === 'awareness') {
        navigate('/ISO-45001-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/ISO-45001-Internal-Auditor-Training');
      } else if (trainingId === 'lead-auditor') {
        navigate('/ISO-45001-Lead-Auditor-Course');
      } else {
        navigate(route);
      }
    }
    // IATF 16949 routes
    else if (standard.slug === 'iatf-16949') {
      if (trainingId === 'awareness') {
        navigate('/IATF-16949-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/IATF-16949-Internal-Auditor-Training');
      } else {
        navigate(route);
      }
    }
    // ISO 13485 routes
    else if (standard.slug === 'iso-13485') {
      if (trainingId === 'awareness') {
        navigate('/ISO-13485-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/ISO-13485-Internal-Auditor-Training');
      } else {
        navigate(route);
      }
    }
    // ISO 22000 routes
    else if (standard.slug === 'iso-22000') {
      if (trainingId === 'awareness') {
        navigate('/ISO-22000-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/ISO-22000-Internal-Auditor-Training');
      } else {
        navigate(route);
      }
    }
    // ISO 50001 routes
    else if (standard.slug === 'iso-50001') {
      if (trainingId === 'awareness') {
        navigate('/ISO-50001-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/ISO-50001-Internal-Auditor-Course');
      } else {
        navigate(route);
      }
    }
    // ISO 21001 routes
    else if (standard.slug === 'iso-21001') {
      if (trainingId === 'awareness') {
        navigate('/ISO-21001-Awareness-Training');
      } else if (trainingId === 'internal-auditor') {
        navigate('/ISO-21001-Internal-Auditor-Training');
      } else if (trainingId === 'lead-auditor') {
        navigate('/ISO-21001-Lead-Auditor-Training');
      } else {
        navigate(route);
      }
    }
    // Default route for other standards
    else {
      navigate(route);
    }
  };

  return (
    <>
      {/* Card */}
      <motion.div
        variants={cardVariants}
        className="relative h-[420px]"
        layoutId={`card-${standard.slug}`}
      >
        <motion.div
          className="relative h-full bg-white rounded-2xl shadow-md cursor-pointer overflow-hidden border-2 border-transparent hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
          onClick={handleCardClick}
          whileHover={{ borderColor: 'rgba(59, 130, 246, 0.2)' }}
        >
          <div className="p-6 h-full flex flex-col">
            {/* Icon with gradient background */}
            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${standard.color.gradient} flex items-center justify-center mb-4 shadow-lg`}>
              <Icon className="h-8 w-8 text-white" />
            </div>

            {/* Badge */}
            <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${standard.color.text} ${standard.color.bg} self-start mb-3`}>
              {standard.code}
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
              {standard.name}
            </h3>

            {/* Gradient underline */}
            <div className={`h-1 w-16 bg-gradient-to-r ${standard.color.gradient} rounded-full mb-4`}></div>

            {/* Description */}
            <p className="text-gray-600 text-sm leading-relaxed flex-1 line-clamp-4">
              {standard.shortDescription}
            </p>

            {/* View Details Link */}
            <div className={`flex items-center gap-2 ${standard.color.text} font-semibold text-sm mt-4`}>
              <span>View Training Options</span>
              <ArrowRight className="h-4 w-4" />
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Expanded Overlay */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            variants={expandedVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleClose}
          >
            <motion.div
              layoutId={`card-${standard.slug}`}
              className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div className={`bg-gradient-to-r ${standard.color.gradient} px-8 py-12 text-white relative`}>
                {/* Close Button */}
                <button
                  onClick={handleClose}
                  className="absolute top-6 right-6 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>

                {/* Standard Info */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="inline-block bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm mb-4 font-semibold">
                    {standard.code}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-2">{standard.name}</h2>
                  <p className="text-lg text-blue-100">{standard.shortDescription}</p>
                </motion.div>
              </div>

              {/* Training Options */}
              <motion.div
                className="p-8"
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.h3
                  variants={itemVariants}
                  className="text-2xl font-bold text-gray-800 mb-6 text-center"
                >
                  Choose Your Training Level
                </motion.h3>

                <div className="grid md:grid-cols-3 gap-6">
                  {trainingLevels.map((training, idx) => {
                    const TrainingIcon = training.icon;
                    return (
                      <motion.div
                        key={training.id}
                        variants={itemVariants}
                        className={`${training.bgColor} rounded-2xl p-6 cursor-pointer border-2 border-transparent hover:border-blue-400 transition-all group`}
                        onClick={() => handleTrainingClick(training.route, training.id)}
                        whileHover={{ scale: 1.05, y: -5 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className={`${training.bgColor} p-4 rounded-xl mb-4`}>
                            <TrainingIcon className={`h-10 w-10 ${training.color}`} />
                          </div>
                          <h4 className="text-lg font-bold text-gray-900 mb-2">
                            {training.title}
                          </h4>
                          <p className={`text-sm font-semibold ${training.color} mb-4`}>
                            {training.duration}
                          </p>
                          <div className="mt-auto">
                            <ArrowRight className={`h-5 w-5 ${training.color} group-hover:translate-x-1 transition-transform`} />
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <motion.div
                  variants={itemVariants}
                  className="mt-8 text-center"
                >
                  <p className="text-sm text-gray-500">
                    Click on any training level to view complete details
                  </p>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
